import React, { useEffect, useState } from "react";
import { Folder, FileText, X, ChevronLeft, ChevronRight, WifiOff } from "lucide-react";
import { live, fileUrl } from "./techData";
import { Screen, Card } from "./ui";

const isImg = (f) => (f.content_type || "").startsWith("image/");
const d10 = (x) => (x || "").slice(0, 10).split("-").reverse().join("/");

function Lightbox({ imgs, at, setAt }) {
  const f = imgs[at];
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="tech-folder-lightbox">
      <div className="flex items-center gap-2 p-3 text-white">
        <div className="flex-1 min-w-0"><div className="text-[16px] font-black truncate">{f.name}</div><div className="text-[13px] text-white/60">{d10(f.created_at)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ""} · {at + 1}/{imgs.length}</div></div>
        <button onClick={() => setAt(null)} data-testid="tech-folder-lightbox-close" className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center"><X className="h-7 w-7" /></button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center relative">
        <img src={fileUrl(f.url)} alt={f.name} className="max-h-full max-w-full object-contain" data-testid="tech-folder-lightbox-img" />
        {at > 0 && <button onClick={() => setAt(at - 1)} data-testid="tech-folder-prev" className="absolute left-2 h-14 w-14 rounded-full bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="h-8 w-8" /></button>}
        {at < imgs.length - 1 && <button onClick={() => setAt(at + 1)} data-testid="tech-folder-next" className="absolute right-2 h-14 w-14 rounded-full bg-white/20 text-white flex items-center justify-center"><ChevronRight className="h-8 w-8" /></button>}
      </div>
    </div>
  );
}

// read-only view of everything in the client's folder (needs signal)
export default function FolderScreen({ clientId, name, nav }) {
  const [files, setFiles] = useState(null), [err, setErr] = useState(""), [dir, setDir] = useState(""), [at, setAt] = useState(null);
  const load = () => {
    if (!navigator.onLine) { setErr("offline"); return; }
    setErr(""); live(`/clients/${clientId}/files`).then(setFiles).catch((e) => setErr(e.message));
  };
  useEffect(load, [clientId]); // eslint-disable-line
  const all = files || [];
  const dirs = [...new Set(all.map((f) => (f.subpath || "").split("/")[0]).filter(Boolean))].sort();
  const shown = all.filter((f) => (dir ? (f.subpath || "").split("/")[0] === dir : true));
  const imgs = shown.filter(isImg), docs = shown.filter((f) => !isImg(f));
  return (
    <Screen title="Client's Folder" sub={name} onBack={nav.back} testid="tech-folder">
      {err === "offline" && <Card className="text-center py-8 space-y-3" data-testid="tech-folder-offline"><WifiOff className="h-10 w-10 mx-auto text-slate-400" /><div className="text-[18px] font-black text-slate-700">Needs phone signal</div><button onClick={load} data-testid="tech-folder-retry" className="h-12 px-6 rounded-xl bg-[#0f2f52] text-white font-black">Try again</button></Card>}
      {err && err !== "offline" && <Card className="text-red-700 font-bold" data-testid="tech-folder-error">{err}</Card>}
      {!files && !err && <Card className="text-center text-slate-500">Loading…</Card>}
      {files && <>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3" data-testid="tech-folder-dirs">
          {[["", `All (${all.length})`], ...dirs.map((x) => [x, x])].map(([k, l]) => <button key={k || "all"} onClick={() => setDir(k)} data-testid={`tech-folder-dir-${k || "all"}`} className={`h-12 px-4 rounded-full whitespace-nowrap text-[15px] font-black inline-flex items-center gap-1.5 ${dir === k ? "bg-[#0f2f52] text-white" : "bg-white text-slate-700 shadow-sm"}`}>{k && <Folder className="h-4 w-4" />}{l}</button>)}
        </div>
        {!shown.length && <Card className="text-center text-slate-500" data-testid="tech-folder-empty">Nothing in this folder.</Card>}
        {imgs.length > 0 && <div className="grid grid-cols-3 gap-2" data-testid="tech-folder-images">
          {imgs.map((f, i) => (
            <button key={f.id} onClick={() => setAt(i)} data-testid={`tech-folder-img-${f.id}`} className="relative aspect-square rounded-xl overflow-hidden bg-slate-200">
              <img src={fileUrl(f.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[11px] font-bold px-1 py-0.5 truncate">{d10(f.created_at)}</span>
            </button>
          ))}
        </div>}
        {docs.map((f) => (
          <a key={f.id} href={fileUrl(f.url)} target="_blank" rel="noreferrer" data-testid={`tech-folder-doc-${f.id}`} className="flex items-center gap-3 bg-white rounded-2xl p-3 shadow-sm">
            <FileText className="h-8 w-8 text-[#0f2f52] shrink-0" />
            <div className="min-w-0"><div className="text-[16px] font-black text-slate-800 truncate">{f.name}</div><div className="text-[13px] text-slate-500">{d10(f.created_at)}{f.subpath ? ` · ${f.subpath}` : ""}</div></div>
          </a>
        ))}
      </>}
      {at !== null && imgs[at] && <Lightbox imgs={imgs} at={at} setAt={setAt} />}
    </Screen>
  );
}
